import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSystemSettings, updateSystemSettings } from '../../services/apiSuperAdmin';

export default function OpenRouterConfigModal({ isOpen, onClose }) {
    const queryClient = useQueryClient();
    const [apiKey, setApiKey] = useState('');
    const [isEditingKey, setIsEditingKey] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [antiban, setAntiban] = useState({});
    const [antibanOk, setAntibanOk] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['system-settings'],
        queryFn: getSystemSettings,
        enabled: isOpen,
    });

    useEffect(() => {
        if (data?.openrouter_api_key) {
            setApiKey(data.openrouter_api_key);
        }
    }, [data]);

    useEffect(() => {
        if (data?.antiban) {
            const plano = {};
            for (const [k, v] of Object.entries(data.antiban)) plano[k] = v.valor;
            setAntiban(plano);
        }
    }, [data]);

    const antibanMutation = useMutation({
        mutationFn: (valores) => updateSystemSettings({ antiban: valores }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-settings'] });
            setAntibanOk(true);
            setTimeout(() => setAntibanOk(false), 3000);
        },
    });

    const ANTIBAN_CAMPOS = [
        ['max_por_conversacion_hora', 'Máx. mensajes a un mismo contacto por hora'],
        ['max_contactos_mismo_texto_hora', 'Máx. contactos con el mismo texto por hora'],
        ['linea_nueva_hora', 'Tope por hora — línea nueva (semana 1)'],
        ['linea_calentando_hora', 'Tope por hora — en calentamiento (semana 2)'],
        ['linea_joven_hora', 'Tope por hora — línea joven (primer mes)'],
        ['linea_madura_hora', 'Tope por hora — línea madura'],
        ['umbral_alerta_volumen_hora', 'Alerta en el log al superar (por hora)'],
    ];

    const mutation = useMutation({
        mutationFn: (key) => updateSystemSettings({ openrouter_api_key: key }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-settings'] });
            setSaveSuccess(true);
            setIsEditingKey(false);
            setTimeout(() => setSaveSuccess(false), 3000);
        },
    });

    if (!isOpen) return null;

    const handleSave = (e) => {
        e.preventDefault();
        mutation.mutate(apiKey);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-950/60 flex items-center justify-center text-purple-600 dark:text-purple-400">
                            <span className="material-symbols-outlined text-2xl">auto_awesome</span>
                        </div>
                        <div>
                            <h3 className="font-bold text-slate-900 dark:text-slate-100 text-lg">
                                Configuración de OpenRouter API
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Clave API Global para optimización e inteligencia de prompts en el Módulo Tutor
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        <span className="material-symbols-outlined text-xl">close</span>
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-6 overflow-y-auto">
                    {/* Status Banner */}
                    <div className={`p-4 rounded-xl flex items-center gap-3 border ${
                        data?.is_openrouter_configured 
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300'
                            : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300'
                    }`}>
                        <span className="material-symbols-outlined text-2xl">
                            {data?.is_openrouter_configured ? 'check_circle' : 'warning'}
                        </span>
                        <div className="text-sm">
                            <p className="font-semibold">
                                {data?.is_openrouter_configured ? 'API Key Configurada y Activa' : 'API Key Pendiente de Configuración'}
                            </p>
                            <p className="text-xs opacity-90 mt-0.5">
                                {data?.is_openrouter_configured 
                                    ? 'El Módulo Tutor puede utilizar modelos de Claude, Gemini, GPT-4o y Grok para optimizar los prompts.' 
                                    : 'Ingresa la clave API de OpenRouter para habilitar el aprendizaje asistido por IA.'}
                            </p>
                        </div>
                    </div>

                    {/* API Key Form */}
                    <form onSubmit={handleSave} className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                                OpenRouter API Key (`sk-or-v1-...`)
                            </label>
                            <div className="relative">
                                <input
                                    type={isEditingKey ? "text" : "password"}
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    placeholder="sk-or-v1-xxxxxxxxxxxxxxxx"
                                    className="w-full px-4 py-3 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                                />
                                <button
                                    type="button"
                                    onClick={() => setIsEditingKey(!isEditingKey)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
                                >
                                    <span className="material-symbols-outlined text-lg">
                                        {isEditingKey ? 'visibility_off' : 'visibility'}
                                    </span>
                                </button>
                            </div>
                        </div>

                        {saveSuccess && (
                            <div className="p-3 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-xl text-xs font-medium flex items-center gap-2">
                                <span className="material-symbols-outlined text-base">check</span>
                                Configuración guardada correctamente.
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={mutation.isLoading}
                                className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold shadow-lg shadow-purple-500/20 disabled:opacity-50 transition-all flex items-center gap-2"
                            >
                                {mutation.isLoading ? (
                                    <>
                                        <span className="animate-spin material-symbols-outlined text-lg">progress_activity</span>
                                        Guardando...
                                    </>
                                ) : (
                                    'Guardar Configuración'
                                )}
                            </button>
                        </div>
                    </form>

                    {/* ── Techos anti-ban de WhatsApp ── */}
                    <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-9 h-9 rounded-xl bg-orange-100 dark:bg-orange-950/60 flex items-center justify-center text-orange-600 dark:text-orange-400">
                                <span className="material-symbols-outlined text-xl">shield</span>
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-slate-100 text-sm">Límites anti-bloqueo de WhatsApp</h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400">Techos globales de envío. Aplican a todas las líneas de todos los tenants.</p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {ANTIBAN_CAMPOS.map(([clave, etiqueta]) => (
                                <div key={clave} className="flex items-center justify-between gap-4">
                                    <label className="text-xs text-slate-600 dark:text-slate-300 leading-snug flex-1">
                                        {etiqueta}
                                        {data?.antiban?.[clave] && (
                                            <span className="block text-[10px] text-slate-400 mt-0.5">Por defecto: {data.antiban[clave].default}</span>
                                        )}
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="500"
                                        value={antiban[clave] ?? ''}
                                        onChange={(e) => setAntiban({ ...antiban, [clave]: e.target.value === '' ? '' : Number(e.target.value) })}
                                        className="w-24 px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500 font-bold text-center"
                                    />
                                </div>
                            ))}
                        </div>

                        {antibanOk && (
                            <div className="mt-3 p-3 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-xl text-xs font-medium flex items-center gap-2">
                                <span className="material-symbols-outlined text-base">check</span>
                                Límites guardados.
                            </div>
                        )}

                        <div className="flex justify-end mt-4">
                            <button
                                type="button"
                                disabled={antibanMutation.isLoading}
                                onClick={() => antibanMutation.mutate(antiban)}
                                className="px-5 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold shadow-lg shadow-orange-500/20 disabled:opacity-50 transition-all flex items-center gap-2"
                            >
                                {antibanMutation.isLoading ? 'Guardando...' : 'Guardar límites'}
                            </button>
                        </div>
                    </div>

                    {/* Available Models Info */}
                    <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
                            Modelos Disponibles en el Módulo Tutor
                        </h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                            {data?.available_models?.map((m) => (
                                <div key={m.id} className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-900/60 border border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                                    <div>
                                        <span className="font-semibold text-slate-800 dark:text-slate-200">{m.name}</span>
                                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-mono">{m.provider}</span>
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{m.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
